<script setup>
import { computed, inject } from 'vue';
import { useRouter } from 'vue-router';
import XlButton from '../xl/XlButton.vue';
import XlSkeleton from '../xl/XlSkeleton.vue';
import XlTag from '../xl/XlTag.vue';
import IconClockForward from '../icons/IconClockForward.vue';
import IconMail from '../icons/IconMail.vue';
import { repeatPattern } from '../../helpers/sessionCard';
import { weekSummary } from '../../helpers/officeHours';
import { clock, dayKey, timeState, weekday, windowUntil } from '../../helpers/sessionTimes';

/**
 * Office Hours on the Sessions page and Home: a drop-in, not a booked
 * session — the prototype's `.now` card, open (ohCardOpen) and closed
 * (ohCardShut), build 7 Oct.
 *
 * Open is inside its window with somebody on the desk; then the one thing to
 * do is join the call. Closed — before its window, after it, or with nobody
 * on — it goes neutral (no green), says so with a clock, gives the desk's
 * weekly schedule and when it next opens, and offers to leave a message
 * instead (XN-4590). It never shows a duration, a headcount, a seat or a
 * reminder.
 *
 * One card wherever it is placed: Sessions and Home (XN-4565) show it from
 * the same slot; away from the Sessions room, Join opens Sessions on it.
 */
const props = defineProps({
  officeHours: { type: Object, default: null },
  agency: { type: String, required: true },
  timeZone: { type: String, required: true },
  now: { type: Date, default: () => new Date() },
  /**
   * The card while its data is on its way: the same card, same lines, with
   * placeholders where the words go — so the tabs and rows under it do not
   * move when it lands (QA: Portal Sessions shifted 167px).
   */
  loading: { type: Boolean, default: false },
});

const room = inject('portalRoom', null);
const router = useRouter();

const host = computed(() => String(props.officeHours?.host?.name ?? '').trim());
const time = computed(() => (props.officeHours ? timeState(props.officeHours, props.now) : {}));
// Somebody has to be on the desk for it to be open.
const isOpen = computed(() => Boolean(props.officeHours) && Boolean(host.value) && time.value.live);
const over = computed(() => Boolean(time.value.ended));

const initials = computed(() => host.value.split(/\s+/).map((word) => word[0]).slice(0, 2).join('').toUpperCase());
const openWindow = computed(() => windowUntil(props.officeHours.starts_at, props.officeHours.ends_at, props.timeZone));

/** "Every weekday, 2:00 PM until 4:00 PM" — the desk's standing hours, when the slot belongs to a series. */
const schedule = computed(() => {
  // The week as the owner set it on the Office Hours page — each day its own
  // hours — on the policyholder's clock. An older payload has only the series'
  // pattern word and the next slot's hours.
  const days = props.officeHours?.schedule;
  if (days?.length) return weekSummary(days, { fromZone: props.officeHours.schedule_zone, toZone: props.timeZone, now: props.now });

  return props.officeHours?.pattern
    ? `${repeatPattern(props.officeHours.pattern, props.officeHours.days)}, ${openWindow.value}`
    : '';
});

const dayAfter = (iso) => {
  const next = new Date(iso);
  next.setUTCDate(next.getUTCDate() + 1);
  return next.toISOString();
};

/** "opens today at 2:00 PM" / "opens tomorrow at …" / "opens Thursday at …" — or, once today's has passed, nothing more to say until the next slot loads. */
const nextOpening = computed(() => {
  if (over.value) return 'closed for today';
  const { starts_at: start } = props.officeHours;
  const startDay = dayKey(start, props.timeZone);
  const today = props.now.toISOString();
  let day = weekday(start, props.timeZone);
  if (startDay === dayKey(today, props.timeZone)) day = 'today';
  else if (startDay === dayKey(dayAfter(today), props.timeZone)) day = 'tomorrow';

  return `opens ${day} at ${clock(start, props.timeZone)}${host.value ? `, with ${host.value} on the desk` : ''}`;
});

/** The closed card's line: the schedule, then the next opening — or, with no series, the opening and its window. */
const closedLine = computed(() => {
  const next = nextOpening.value;
  if (schedule.value) return `${schedule.value} · ${next}.`;
  if (over.value) return `Someone from ${props.agency} is on the desk for a drop-in call. Closed for today.`;

  return `Someone from ${props.agency} is on the desk for a drop-in call, ${openWindow.value}. ${next.charAt(0).toUpperCase()}${next.slice(1)}.`;
});

const leaveMessage = () => router?.push('/hub/messages');

const join = () => (room
  ? room.join(props.officeHours.id)
  : router.push({ path: '/hub/sessions', query: { session: props.officeHours.id } }));
</script>

<template>
  <!-- Loading: each placeholder sits on the line its words will, so the card
       is the height it is about to be. -->
  <section v-if="loading" class="oh oh--shut oh--loading" aria-busy="true" data-testid="office-hours-loading">
    <span class="oh__ph oh__ph--loading" aria-hidden="true" />
    <div class="oh__in">
      <span class="oh__lbl"><XlSkeleton width="210px" height="0.9em" radius="6px" class="oh__sk" /></span>
      <h2 class="oh__h"><XlSkeleton width="62%" height="0.9em" radius="7px" class="oh__sk" /></h2>
      <p class="oh__p">
        <XlSkeleton width="94%" height="0.9em" radius="6px" class="oh__sk" />
        <XlSkeleton width="58%" height="0.9em" radius="6px" class="oh__sk" />
      </p>
    </div>
    <div class="oh__act">
      <XlSkeleton width="150px" height="40px" radius="99px" class="oh__sk oh__sk--button" />
    </div>
  </section>

  <section v-else-if="officeHours" class="oh" :class="{ 'oh--shut': !isOpen }" data-testid="office-hours">
    <span class="oh__ph" aria-hidden="true">
      <template v-if="isOpen && initials">
        <img v-if="officeHours.host.photo" :src="officeHours.host.photo" alt="" class="oh__photo" data-testid="office-hours-host-photo" />
        <template v-else>{{ initials }}</template>
        <i class="oh__dot" />
      </template>
      <IconClockForward v-else color="currentColor" size="26" />
    </span>
    <div class="oh__in">
      <span class="oh__lbl" data-testid="office-hours-status">
        <template v-if="isOpen">● Office Hours · open until {{ clock(officeHours.ends_at, timeZone) }}</template>
        <template v-else><IconClockForward color="currentColor" size="13" class="oh__lbl-icon" />Office Hours · closed right now</template>
      </span>
      <h2 v-if="isOpen" class="oh__h">
        Office Hours — {{ host }} is on the desk today
        <XlTag tone="success" data-testid="office-hours-dropin">Drop-in</XlTag>
      </h2>
      <h2 v-else class="oh__h">Drop-in Office Hours</h2>
      <p v-if="isOpen" class="oh__p" data-testid="office-hours-opens">
        {{ openWindow }}. Drop in and ask anything — no booking, camera off unless you turn it on. Nothing is recorded.
      </p>
      <p v-else class="oh__p" data-testid="office-hours-opens">{{ closedLine }}</p>
    </div>
    <div class="oh__act">
      <XlButton v-if="isOpen" variant="success" data-testid="office-hours-join" @click="join">Join the call</XlButton>
      <XlButton v-else variant="ghost" data-testid="office-hours-message" @click="leaveMessage">
        <IconMail color="currentColor" size="16" /> Leave a message
      </XlButton>
    </div>
  </section>
</template>

<style scoped>
/* Prototype `.now` (build 7 Oct): green while open, grey while shut (ohCardShut). */
.oh {
  position: relative; overflow: hidden; display: flex; align-items: center; gap: 18px;
  padding: 22px 24px; margin: 0 0 26px; border-radius: 24px;
  background: linear-gradient(135deg, #E9F6EF, #F6FCF9); border: 1px solid #C0E3D0;
}
/* First stop is the prototype's --pur-2 (#8154A6), which is surface-tint here;
   primary-fixed-dim is the pale avatar tint (#EADFF3) in this theme. */
.oh__ph {
  width: 62px; height: 62px; flex: 0 0 62px; border-radius: 50%; display: grid; place-items: center;
  background: linear-gradient(135deg, var(--xl-color-surface-tint), var(--xl-color-primary));
  color: #fff; font-size: 19px; font-weight: 600;
}
.oh--shut { background: linear-gradient(135deg, #F6F2F8, #FCFAFD); border-color: var(--xl-color-border); }
/* Closed, the avatar keeps the prototype's pale tint (build 12 Oct 17:52), not a grey ring (XN-4736). */
.oh--shut .oh__ph { background: var(--xl-color-primary-fixed-dim); color: var(--xl-color-on-primary-container); }
.oh--shut .oh__lbl { color: var(--xl-color-secondary-text); }
.oh__lbl-icon { vertical-align: -2px; margin-right: 5px; }
.oh__ph { position: relative; }
.oh__photo { width: 100%; height: 100%; border-radius: 50%; object-fit: cover; }
.oh__dot { position: absolute; right: 2px; bottom: 2px; width: 13px; height: 13px; border-radius: 50%; background: #22A05B; border: 2px solid #fff; }
.oh__h { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.oh__in { flex: 1; min-width: 0; }
.oh__lbl { font-size: 11.5px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: #166B48; }
.oh__h { margin: 4px 0 4px; font-size: 20px; font-weight: 600; letter-spacing: -0.02em; }
.oh__p { margin: 0; font-size: 14.5px; color: var(--xl-color-on-surface-variant); }
.oh__act { flex: 0 0 auto; display: flex; gap: 10px; align-items: center; flex-wrap: wrap; }
/* Loading: each placeholder stays inline, one to a line, so every line keeps
   the height its text gives it and the card is the height it will be. */
.oh__ph--loading { background: var(--xl-color-border, #EDE7F0); }
.oh__sk { vertical-align: middle; }
.oh__sk--button { width: 150px; }

@media (max-width: 600px) {
  .oh { flex-direction: column; align-items: flex-start; }
}
</style>
